import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { toast } from 'sonner';
import { 
  Sparkles, Wand2, Hash, Copy, Check, ChevronDown, ChevronUp, 
  RefreshCw, Lightbulb, AlertCircle, Info, Zap
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Platform-specific configuration
const PLATFORMS = {
  linkedin: { 
    name: 'LinkedIn', 
    icon: '💼', 
    color: 'bg-blue-600',
    description: 'Professional, thought-leadership. 600-900 chars.'
  },
  facebook: { 
    name: 'Facebook', 
    icon: '📘', 
    color: 'bg-blue-500',
    description: 'Short, skimmable. 150-500 chars.'
  },
  twitter: { 
    name: 'Twitter/X', 
    icon: '🐦', 
    color: 'bg-slate-800',
    description: 'Punchy, 230-260 chars. Max 280.'
  }
};

// Improvement options
const IMPROVEMENT_OPTIONS = [
  { value: 'shorter', label: 'Make Shorter', icon: '✂️' },
  { value: 'longer', label: 'Expand', icon: '📝' },
  { value: 'more_engaging', label: 'More Engaging', icon: '🎯' },
  { value: 'more_professional', label: 'More Professional', icon: '👔' },
  { value: 'add_cta', label: 'Add CTA', icon: '👉' }
];

/**
 * AIGeneratePanel - Component for AI caption and hashtag generation
 * 
 * Phase 9: AI Marketing Intelligence
 * All outputs are drafts that must go through approval workflow.
 */
export const AIGeneratePanel = ({
  designId,
  designName,
  designDescription,
  currentCaption,
  onSelectCaption,
  onSelectHashtags,
  isOpen = true,
  onToggle
}) => {
  const [platform, setPlatform] = useState('linkedin');
  const [additionalContext, setAdditionalContext] = useState('');
  const [variantCount, setVariantCount] = useState(3);
  
  const [generatedCaptions, setGeneratedCaptions] = useState([]);
  const [generatedHashtags, setGeneratedHashtags] = useState([]);
  const [improvedCaptions, setImprovedCaptions] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [loadingHashtags, setLoadingHashtags] = useState(false);
  const [loadingImprove, setLoadingImprove] = useState(false);
  
  const [selectedCaptionId, setSelectedCaptionId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [showMetadata, setShowMetadata] = useState(false);

  // Generate captions
  const generateCaptions = useCallback(async () => {
    setLoading(true);
    setGeneratedCaptions([]);
    setSelectedCaptionId(null);
    
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await axios.post(
        `${API}/marketing/ai/generate-captions`,
        {
          design_id: designId,
          design_name: designName,
          design_description: designDescription,
          platform,
          context: additionalContext || undefined,
          variant_count: variantCount
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.variants) {
        setGeneratedCaptions(response.data.variants);
        toast.success(`Generated ${response.data.variants.length} caption variants`);
      }
    } catch (error) {
      console.error('Caption generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate captions');
    } finally {
      setLoading(false);
    }
  }, [designId, designName, designDescription, platform, additionalContext, variantCount]);

  // Improve existing caption
  const improveCaption = useCallback(async (focus = null) => {
    if (!currentCaption?.trim()) {
      toast.error('No caption to improve');
      return;
    }
    
    setLoadingImprove(true);
    setImprovedCaptions([]);
    
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await axios.post(
        `${API}/marketing/ai/improve-caption`,
        {
          original_caption: currentCaption,
          platform,
          improvement_focus: focus,
          context: additionalContext || undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.improved_variants) {
        setImprovedCaptions(response.data.improved_variants);
        toast.success('Caption improved!');
      }
    } catch (error) {
      console.error('Caption improvement error:', error);
      toast.error(error.response?.data?.detail || 'Failed to improve caption');
    } finally {
      setLoadingImprove(false);
    }
  }, [currentCaption, platform, additionalContext]);

  // Generate hashtags
  const suggestHashtags = useCallback(async () => {
    setLoadingHashtags(true);
    setGeneratedHashtags([]);
    
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await axios.post(
        `${API}/marketing/ai/suggest-hashtags`,
        {
          caption: currentCaption || generatedCaptions[0]?.caption,
          design_name: designName,
          platform,
          context: additionalContext || undefined,
          count: 10
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.hashtags) {
        setGeneratedHashtags(response.data.hashtags);
        toast.success(`Generated ${response.data.hashtags.length} hashtag suggestions`);
      }
    } catch (error) {
      console.error('Hashtag generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate hashtags');
    } finally {
      setLoadingHashtags(false);
    }
  }, [currentCaption, generatedCaptions, designName, platform, additionalContext]);

  // Copy caption to clipboard
  const copyCaption = useCallback((caption, id) => {
    navigator.clipboard.writeText(caption);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success('Caption copied!');
  }, []);

  // Select caption to use
  const handleSelectCaption = useCallback((caption) => {
    setSelectedCaptionId(caption.id);
    if (onSelectCaption) {
      onSelectCaption(caption.caption);
    }
    toast.success('Caption selected - remember to submit for approval');
  }, [onSelectCaption]);

  // Select hashtags to use
  const handleSelectHashtags = useCallback((hashtags) => {
    const hashtagStrings = hashtags.map(h => h.hashtag);
    if (onSelectHashtags) {
      onSelectHashtags(hashtagStrings);
    }
    toast.success('Hashtags selected');
  }, [onSelectHashtags]);

  if (!isOpen) {
    return (
      <div 
        className="p-3 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Sparkles className="h-4 w-4 text-purple-500" />
          <span>AI Assistant</span>
          <ChevronDown className="h-4 w-4 ml-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-500" />
          <span className="font-semibold dark:text-white">AI Assistant</span>
          <Badge variant="outline" className="text-xs">Beta</Badge>
        </div>
        <ChevronUp className="h-4 w-4 text-muted-foreground" />
      </div>

      {/* Platform Selector */}
      <div className="space-y-2">
        <Label className="text-sm dark:text-slate-300">Target Platform</Label>
        <div className="flex gap-2">
          {Object.entries(PLATFORMS).map(([key, config]) => (
            <TooltipProvider key={key}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={platform === key ? "default" : "outline"}
                    size="sm"
                    onClick={() => setPlatform(key)}
                    className={platform === key ? config.color : ''}
                  >
                    <span className="mr-1">{config.icon}</span>
                    {config.name}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{config.description}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>
      </div>

      {/* Context Input */}
      <div className="space-y-2">
        <Label className="text-sm dark:text-slate-300">Additional Context (Optional)</Label>
        <Textarea
          value={additionalContext}
          onChange={(e) => setAdditionalContext(e.target.value)}
          placeholder="Add context about the content, target audience, or campaign goals..."
          className="h-16 text-sm dark:bg-slate-700"
        />
      </div>

      <Separator />

      {/* Generate Captions Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-purple-500" />
            <span className="font-medium dark:text-white text-sm">Generate Captions</span>
          </div>
          <Select 
            value={variantCount.toString()} 
            onValueChange={(v) => setVariantCount(parseInt(v))}
          >
            <SelectTrigger className="w-24 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">3 variants</SelectItem>
              <SelectItem value="4">4 variants</SelectItem>
              <SelectItem value="5">5 variants</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <Button 
          onClick={generateCaptions} 
          disabled={loading}
          className="w-full bg-purple-600 hover:bg-purple-700"
          data-testid="generate-captions-btn"
        >
          {loading ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4 mr-2" />
          )}
          {loading ? 'Generating...' : 'Generate Caption Variants'}
        </Button>

        {/* Generated Captions */}
        {generatedCaptions.length > 0 && (
          <ScrollArea className="h-64">
            <div className="space-y-3 pr-3">
              {generatedCaptions.map((caption, idx) => (
                <Card 
                  key={caption.id}
                  className={`cursor-pointer transition-all ${
                    selectedCaptionId === caption.id 
                      ? 'ring-2 ring-purple-500 bg-purple-50 dark:bg-purple-900/20' 
                      : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                  }`}
                  data-testid={`caption-variant-${idx}`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm dark:text-slate-200 flex-1">
                        {caption.caption}
                      </p>
                      <div className="flex gap-1 flex-shrink-0">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyCaption(caption.caption, caption.id);
                                }}
                              >
                                {copiedId === caption.id ? (
                                  <Check className="h-3 w-3 text-green-500" />
                                ) : (
                                  <Copy className="h-3 w-3" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Copy</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectCaption(caption);
                          }}
                        >
                          Use
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge variant="secondary" className="text-xs">
                        {caption.character_count} chars
                      </Badge>
                      <Badge variant="outline" className="text-xs capitalize">
                        {caption.tone}
                      </Badge>
                      <Badge variant="outline" className="text-xs capitalize">
                        {caption.structure}
                      </Badge>
                      {showMetadata && (
                        <Badge variant="outline" className="text-xs text-slate-400">
                          {caption.provider}/{caption.model}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        )}

        {/* Show metadata toggle */}
        {generatedCaptions.length > 0 && (
          <button
            className="text-xs text-muted-foreground hover:text-slate-600 dark:hover:text-slate-300 flex items-center gap-1"
            onClick={() => setShowMetadata(!showMetadata)}
          >
            <Info className="h-3 w-3" />
            {showMetadata ? 'Hide' : 'Show'} metadata
          </button>
        )}
      </div>

      <Separator />

      {/* Improve Caption Section */}
      {currentCaption && (
        <>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-amber-500" />
              <span className="font-medium dark:text-white text-sm">Improve Current Caption</span>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {IMPROVEMENT_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  variant="outline"
                  size="sm"
                  onClick={() => improveCaption(option.value)}
                  disabled={loadingImprove}
                  className="text-xs"
                  data-testid={`improve-${option.value}-btn`}
                >
                  <span className="mr-1">{option.icon}</span>
                  {option.label}
                </Button>
              ))}
            </div>

            {loadingImprove && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Improving caption...
              </div>
            )}

            {/* Improved Captions */}
            {improvedCaptions.length > 0 && (
              <div className="space-y-2">
                {improvedCaptions.map((caption, idx) => (
                  <Card 
                    key={caption.id}
                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50"
                  >
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm dark:text-slate-200 flex-1">
                          {caption.caption}
                        </p>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => copyCaption(caption.caption, caption.id)}
                          >
                            {copiedId === caption.id ? (
                              <Check className="h-3 w-3 text-green-500" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => handleSelectCaption(caption)}
                          >
                            Use
                          </Button>
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-xs mt-2">
                        {caption.character_count} chars
                      </Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          <Separator />
        </>
      )}

      {/* Hashtag Suggestions Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Hash className="h-4 w-4 text-blue-500" />
          <span className="font-medium dark:text-white text-sm">Hashtag Suggestions</span>
        </div>
        
        <Button 
          onClick={suggestHashtags} 
          disabled={loadingHashtags}
          variant="outline"
          className="w-full"
          data-testid="suggest-hashtags-btn"
        >
          {loadingHashtags ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Hash className="h-4 w-4 mr-2" />
          )}
          {loadingHashtags ? 'Generating...' : 'Suggest Hashtags'}
        </Button>

        {/* Generated Hashtags */}
        {generatedHashtags.length > 0 && (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {generatedHashtags.map((hashtag, idx) => (
                <Badge 
                  key={idx}
                  variant={hashtag.relevance === 'high' ? 'default' : 'secondary'}
                  className={`cursor-pointer hover:opacity-80 ${
                    hashtag.relevance === 'high' ? 'bg-blue-600' : ''
                  }`}
                  onClick={() => {
                    navigator.clipboard.writeText(hashtag.hashtag);
                    toast.success(`Copied: ${hashtag.hashtag}`);
                  }}
                  data-testid={`hashtag-${idx}`}
                >
                  {hashtag.hashtag}
                  {hashtag.relevance === 'high' && (
                    <span className="ml-1 opacity-60">★</span>
                  )}
                </Badge>
              ))}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={() => handleSelectHashtags(generatedHashtags)}
            >
              Use All Hashtags
            </Button>
          </div>
        )}
      </div>

      {/* Draft Notice */}
      <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-xs">
        <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="text-amber-700 dark:text-amber-300">
          <strong>Draft Content:</strong> All AI-generated content is a draft and must go through the approval workflow before publishing.
        </div>
      </div>
    </div>
  );
};

export default AIGeneratePanel;
